package nl.volleyplanner.web.controller.planner

import kotlinx.browser.document
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.*
import kotlinx.datetime.format
import kotlinx.dom.addClass
import kotlinx.dom.removeClass
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.div
import kotlinx.html.js.onClickFunction
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.api.files.MemberImportFileUploadResponse
import nl.volleyplanner.api.planner.PlannerPrerequisites
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.VolleyPlannerApplication
import nl.volleyplanner.web.base.*
import nl.volleyplanner.web.base.component.bootstrapAlert
import nl.volleyplanner.web.base.routing.NavigationManager
import nl.volleyplanner.web.component.FileUploadComponent
import nl.volleyplanner.web.controller.LedenController
import nl.volleyplanner.web.launchAsync
import nl.volleyplanner.web.service.WebUserAndOrganisationService
import nl.volleyplanner.web.util.BootstrapColor
import nl.volleyplanner.web.util.LocalDateTimeFormatters
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

class MemberImportStatusComponent(
    private val memberOverviewController: LedenController
) : HTMLComponent() {
    private val prerequisitesFlow = MutableStateFlow<PlannerPrerequisites?>(null)

    init {
        launchAsync {
            VolleyPlannerApplication.volleyplannerApiClient.organisation.plannerConfiguration.getPlannerPrerequisites()
        }.onComplete { prerequisitesFlow.value = it }
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.div(classes = "row border align-items-stretch mt-2 rounded-top-1") {
            style = "max-width: 600px;"
            div(classes = "col") {
                div(classes = "row py-2 text-bg-primary bg-gradient d-flex align-items-center") {
                    h4(classes = "col m-0 fw-semibold") { +"Huidige status" }
                }
                div (classes = "row py-2 align-items-center border-bottom border-secondary-subtle") {
                    div(classes = "col") {
                        style = "width: 200px; max-width: 200px;"
                        strong { +"Aantal leden" }
                    }
                    div(classes = "col-auto") {
                        if (prerequisitesFlow.value == null) { spinner() } else {
                            strong { +"${prerequisitesFlow.value!!.memberCount}" }
                            +" leden in systeem"
                            a (classes = "btn btn-primary ms-2") {
                                href = NavigationManager.state.value.state.path + memberOverviewController.path
                                +"Overzicht"
                            }
                        }
                    }
                }
                div (classes = "row py-2 align-items-center") {
                    div(classes = "col") {
                        style = "width: 200px; max-width: 200px;"
                        strong { +"Laatste upload" }
                    }
                    div(classes = "col-auto") {
                        if (prerequisitesFlow.value == null) { spinner() } else {
                            +(prerequisitesFlow.value!!.lastMemberUpload?.format(LocalDateTimeFormatters.DAY_MONTH_YEAR_TIME_TEXT_SHORT)
                                ?: "Geen eerdere uploads gevonden")
                        }
                    }
                }
            }
        }
    }

    override suspend fun getState() = prerequisitesFlow
}

class MemberImportComponent(
    private val memberOverviewController: LedenController
) : HTMLComponent() {
    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val activeSeason = WebUserAndOrganisationService.selectedOrganisationState.value?.activeSeason?.value
        val availableSeasons = WebUserAndOrganisationService.selectedOrganisationState.value?.availableSeasons?.value
        return consumer.div {
            h1 (classes = "display-5") { +"Leden importeren" }
            p (classes = "lead") {
                +"Upload alle leden, inclusief leden/mensen die geen onderdeel zijn van een team maar die je wel wil kunnen toewijzen als scheidsrechter of teller bij een wedstrijd."
            }

            component(MemberImportStatusComponent(memberOverviewController))

            val firstcolClasses = "col-12 col-sm-3"
            val secondcolClasses = "col-12 col-sm-9"
            div(classes = "row border align-items-stretch mt-5 rounded-top-1") {
                style = "max-width: 850px;"
                div(classes = "col") {
                    div(classes = "row py-2 text-bg-primary bg-gradient d-flex align-items-center") {
                        h4(classes = "col m-0 fw-semibold") { +"Bestand specificaties" }
                    }
                    div (classes = "row py-2 align-items-center border-bottom border-secondary-subtle") {
                        div(classes = firstcolClasses) {
                            strong { +"Template" }
                        }
                        div(classes = secondcolClasses) {
                            button(classes = "btn btn-primary") {
                                type = ButtonType.button
                                onClickFunction = { event ->
                                    val buttonElement = event.currentTarget as HTMLButtonElement
                                    buttonElement.disabled = true
                                    buttonElement.removeClass("btn-primary")
                                    buttonElement.addClass("btn-secondary")
                                    launchAsync(CoroutineManager.RENDER_SCOPE) {
                                        // TODO: Make file types a constant
                                        VolleyPlannerApplication.volleyplannerApiPlannerClient.downloadFileTemplate("members_import")
                                    }.onComplete { (fileName, byteArray) ->
                                        URL.createObjectURL(
                                            Blob(arrayOf(byteArray), BlobPropertyBag("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"))
                                        ).let { objectURL ->
                                            (document.createElement("a") as HTMLAnchorElement)
                                                .apply {
                                                    href = objectURL
                                                    download = fileName
                                                }.click()
                                            URL.revokeObjectURL(objectURL)
                                        }

                                        buttonElement.disabled = false
                                        buttonElement.removeClass("btn-secondary")
                                        buttonElement.addClass("btn-primary")
                                    }.onError { errorMessage ->
                                        buttonElement.disabled = false
                                        buttonElement.removeClass("btn-secondary")
                                        buttonElement.addClass("btn-danger")
                                    }
                                }
                                +"Download Template"
                            }
                        }
                    }
                    div (classes = "row py-2 align-items-center border-bottom border-secondary-subtle") {
                        div(classes = firstcolClasses) {
                            strong { +"Ondersteunde formaten" }
                        }
                        div(classes = secondcolClasses) {
                            +"xls, xlsx, csv"
                            br {}
                            span(classes = "small text-secondary") {
                                +"Excel: enkel het eerste (meest linker) blad wordt ingelezen"
                            }
                        }
                    }
                    div(classes = "row py-2 bg-secondary-subtle bg-gradient d-flex align-items-center") {
                        h6(classes = "col m-0 fw-semibold") { +"Kolommen" }
                    }
                    div (classes = "row py-2 align-items-center border-bottom border-secondary-subtle") {
                        div(classes = firstcolClasses) {
                            strong { +"Lidnummer" }
                        }
                        div(classes = secondcolClasses) {
                            +"Een uniek nummer (bv uit de ledenadministratie) voor het lid."
                            br {}
                            span (classes = "small text-secondary") {
                                +"Unieke code voor een lid ten behoeve van latere updates. "
                                br {}
                                +"Gebruik niet de nevobo relatiecode. Voor nieuwe leden is deze meestal niet direct bekend. En verenigingsleden hebben deze mogelijk niet"
                            }
                        }
                    }
                    div (classes = "row py-2 align-items-center border-bottom border-secondary-subtle") {
                        div(classes = firstcolClasses) {
                            strong { +"Naam" }
                        }
                        div(classes = secondcolClasses) {
                            +"De naam van het lid"
                            br {}
                            span (classes = "small text-secondary") {
                                +"Wordt afgekort tot voornaam + teamnummer in definitieve schema's"
                            }
                        }
                    }
                    div (classes = "row py-2 align-items-center border-bottom border-secondary-subtle") {
                        div(classes = firstcolClasses) {
                            strong { +"Team" }
                        }
                        div(classes = secondcolClasses) {
                            +"De team(s) waar het lid in speelt"
                            br {}
                            span (classes = "small text-secondary") {
                                +"Gebruik de Nevobo naamgeving: bv '<clubnaam> DS 1'."
                                br {}
                                +"Teamsponsoren als 'Mijnsponsor <clubnaam> DS 1' zijn niet verplicht"
                                br {}
                                +"Voeg een lid aan meerdere teams toe met extra 'Team' kolommen."
                                br {}
                                +"Kan leeg gelaten worden voor verenigingsleden e.d."
                            }
                        }
                    }
                    div (classes = "row py-2 align-items-center border-bottom border-secondary-subtle") {
                        div(classes = firstcolClasses) {
                            strong { +"Scheidsrechter" }
                        }
                        div(classes = secondcolClasses) {
                            +"De scheidsrechtersgroep waar het lid toe behoort"
                            br {}
                            span (classes = "small text-secondary") {
                                +"Ondersteunde groepen op basis van Nevobo codes: "
                                ul {
                                    li { +"V2 (promotieklasse en hoger)" }
                                    li { +"V4 (1ste & 2de klasse senioren + Topklasse jeugd)" }
                                    li { +"V6 (3de klasse senioren en lager + overige jeugd)" }
                                }
                                +"Neem even contact op als je extra groepen nodig hebt."
                            }
                        }
                    }
                }
            }

            div (classes = "mt-2 border rounded-2 p-2") {
                h5 (classes = "pb-1 border-bottom") { +"Voorbeeld" }
                table (classes = "table") {
                    thead {
                        tr {
                            th(scope = ThScope.col) { +"Lidnummer" }
                            th(scope = ThScope.col) { +"Naam" }
                            th(scope = ThScope.col) { +"Team" }
                            th(scope = ThScope.col) { +"Team" }
                            th(scope = ThScope.col) { +"Scheidsrechter" }
                        }
                    }
                    tbody {
                        tr {
                            td { +"1000101" }
                            td { +"Lisa de Vries" }
                            td { +"Mijn Vereniging DS 1" }
                            td { +"" }
                            td { +"V4" }
                        }
                        tr {
                            td { +"1000102" }
                            td { +"Emma Jansen" }
                            td { +"Mijn Vereniging DS 1" }
                            td { +"" }
                            td { +"V6" }
                        }
                        tr {
                            td { +"1000103" }
                            td { +"Sophie Willems" }
                            td { +"Mijn Vereniging MA 1" }
                            td { +"Mijn Vereniging DS 3" }
                            td { +"" }
                        }
                        tr {
                            td { +"1000105" }
                            td { +"Bram van Dijk" }
                            td { +"Mijn Vereniging HS 6" }
                            td { +"" }
                            td { +"V4" }
                        }
                        tr {
                            td { +"1000104" }
                            td { +"Jan Bakker" }
                            td { +"Mijn Vereniging HS 7" }
                            td { +"Mijn Vereniging HS 6" }
                            td { +"V6" }
                        }
                    }
                }
            }
            h4 (classes = "mt-4") {
                +"Upload ledenbestand"
            }
            bootstrapAlert(BootstrapColor.Warning) {
                classes += "mt-2"

                p {
                    unsafe { +"Voeg altijd een <strong>kopregel</strong> toe met de kolomnamen." }
                    br {}
                    +"Ondersteunde kolomnamen zoals hierboven beschreven: "
                    strong { +"Lidnummer, Naam, Team, Scheidsrechter" }
                }
                p {
                    unsafe { +"<strong>Let op!</strong> upload altijd een volledig ledenbestand. Alle huidige leden in het systeem worden geupdate/vervangen." }
                }
            }

            if (activeSeason == null) {
                spinner()
            } else {
                if (availableSeasons!!.size > 1) {
                    div (classes = "row py-2 align-items-center") {
                        div(classes = "col-12 col-sm-3") {
                            strong { +"Upload voor seizoen" }
                        }
                        div(classes = "col-12 col-sm-9") {
                            div {
                                strong { +activeSeason.name }
                            }
                            div (classes = "small text-secondary") {
                                +"Teams en scheisdrechters uit het bestand zijn alleen van toepassing op dit seizoen"
                            }
                        }
                    }
                }

                component(FileUploadComponent<MemberImportFileUploadResponse>(
                    "member-upload", "members_import", listOf("xls", "xlsx", "csv"),
                    MemberImportFileUploadResponse.serializer(),
                    { response ->
                        dl (classes = "row") {
                            dt (classes = "col-3") { +"Totaal leden" }
                            dd (classes = "col-9") { +"${response.details!!.membersTotal}" }
                            dt (classes = "col-3") { +"Nieuwe leden" }
                            dd (classes = "col-9") { +"${response.details!!.membersCreated}" }
                            dt (classes = "col-3") { +"Leden geupdate" }
                            dd (classes = "col-9") { +"${response.details!!.membersUpdated}" }
                            dt (classes = "col-3") { +"Leden gedeactiveerd" }
                            dd (classes = "col-9") { +"${response.details!!.membersDeleted}" }
                        }
                    },
                    customFields = mapOf(
                        "organisation_season" to activeSeason.name
                    )
                ))
            }
        }
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    override suspend fun getState() = WebUserAndOrganisationService.selectedOrganisationState
        .filterNotNull()
        .flatMapLatest { it.activeSeason }
        .stateIn(CoroutineManager.RENDER_SCOPE, SharingStarted.Eagerly, WebUserAndOrganisationService.selectedOrganisationState.value?.activeSeason?.value)
}

class MemberImportController(override val parent: PlannerIndexController) : StaticPageController() {
    override val name = "MemberImportController"
    override val title = "Importeer leden"
    override val path = "/importeren/leden"
    override val requiredAuthLevel = AuthLevel.AdminUser

    val memberOverviewController = LedenController(this)

    override val childPages = listOf<WebPageController>(
        memberOverviewController
    )

    override fun createRuntimeInstance() = MemberImportController(parent)

    override suspend fun renderContent(): HTMLElement {
        return document.create.div {
            component(MemberImportComponent(memberOverviewController))
        }
    }
}